/**
 * 中枢决策详情：她**为什么**这么答。
 *
 * 一次请求可以有多轮模型调用，每轮用不同模型、不同单价：
 *   round=1  deepseek-v4-pro  决定调用 weather 工具
 *   round=2  qwen3.7-flash    消化天气结果
 * 因此成本必须按 LlmCall 逐条核算，不能塞进一行。
 *
 * ## prompt 原文需要 `prompt.read`
 *
 * 里面既有角色设定全文（商业机密），也有拼进上下文的用户消息（个人信息）。
 * 没有该权限时后端返回 null 并置 `promptRedacted`，这里显示为提示条。
 * **有权限时查看会写一条 AuditLog。**
 */

import { Alert, Card, Col, Collapse, Descriptions, Row, Space, Statistic, Table, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../../services/api";
import { fmtCents, fmtDateTime, fmtNumber, fmtPercent, StatusTag, Tags } from "../../components/format";

interface LlmCall {
  id: string;
  round: number;
  purpose: string;
  capability: string;
  resolvedModel: string;
  systemPrompt: string | null;
  taskPrompt: string | null;
  systemPromptLength: number;
  taskPromptLength: number;
  analysis: unknown;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  costCents: number;
  latencyMs: number;
  finishReason: string | null;
  errorCode: string | null;
  retentionUntil: string | null;
  createdAt: string | null;
}

interface RunDetail {
  id: string;
  relationshipId: string | null;
  conversationId: string | null;
  trigger: string;
  formKey: string | null;
  inputMessageId: string | null;
  outputMessageId: string | null;
  detectedIntent: string | null;
  intentConfidence: number | null;
  safetyVerdict: string;
  recalledMemoryIds: string[];
  recalledChunkIds: string[];
  contextChannelEventIds: string[];
  contextMessageCount: number;
  toolCallRounds: number;
  totalInputTokens: number;
  totalCachedTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  latencyMs: number;
  status: string;
  triggeredByUntrustedContent: boolean;
  deidentificationChecked: boolean;
  createdAt: string | null;
  promptRedacted: boolean;
  calls: LlmCall[];
  skillExecutions: {
    id: string;
    skillKey: string;
    tier: string;
    status: string;
    reservedPower: number;
    settledPower: number;
    errorCode: string | null;
  }[];
  toolExecutions: {
    id: string;
    round: number;
    toolKey: string;
    status: string;
    latencyMs: number;
    costCents: number;
    errorCode: string | null;
  }[];
  moderationDecisions: {
    id: string;
    targetType: string;
    verdict: string;
    categories: string[];
    confidence: number | null;
    decidedBy: string;
  }[];
}

export function BrainRunDetailPage() {
  const { id = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["brainRun", id],
    queryFn: () => api.get<RunDetail | null>(`/api/v1/admin/brain/runs/${id}`),
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;
  if (!isLoading && !data) return <Alert type="warning" showIcon message="决策记录不存在" />;

  const cacheRate = data && data.totalInputTokens > 0 ? data.totalCachedTokens / data.totalInputTokens : 0;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {data?.triggeredByUntrustedContent && (
        <Alert
          type="warning"
          showIcon
          message="本次决策链路中出现过外部抓取的不可信内容"
          description="防的是间接提示注入。为 true 时强制：禁止执行 guarded Skill、禁止写记忆、禁止改关系值、禁止发通知。"
        />
      )}

      <Card title="中枢决策" loading={isLoading} styles={{ body: { padding: 16 } }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={4}>
            <Statistic title="输入 token" value={fmtNumber(data?.totalInputTokens)} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic
              title="缓存命中"
              value={fmtPercent(cacheRate)}
              valueStyle={{ color: cacheRate < 0.3 ? "#B4232E" : "#2F7A4B" }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="输出 token" value={fmtNumber(data?.totalOutputTokens)} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="花费" value={fmtCents(data?.totalCostCents)} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="延迟" value={`${data?.latencyMs ?? 0}ms`} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="模型调用" value={data?.calls.length ?? 0} />
          </Col>
        </Row>

        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="状态">
            <StatusTag value={data?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="形态">
            <StatusTag value={data?.formKey} />
          </Descriptions.Item>
          <Descriptions.Item label="触发源">{data?.trigger}</Descriptions.Item>
          <Descriptions.Item label="安全判定">
            <StatusTag value={data?.safetyVerdict} />
          </Descriptions.Item>
          <Descriptions.Item label="识别意图">
            {data?.detectedIntent ?? "—"}
            {data?.intentConfidence !== null && data?.intentConfidence !== undefined
              ? `（${data.intentConfidence}）`
              : ""}
          </Descriptions.Item>
          <Descriptions.Item label="去标识化检查">
            {data?.deidentificationChecked ? <Tag color="green">已通过</Tag> : <Tag>未涉及</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="上下文消息数">{data?.contextMessageCount}</Descriptions.Item>
          <Descriptions.Item label="工具轮次">
            {data?.toolCallRounds} / 3
            <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
              上限防止 search → search → search 的无限递归
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="时间">{fmtDateTime(data?.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="会话">
            {data?.conversationId ? (
              <Link to={`/conversations/${data.conversationId}`}>{data.conversationId.slice(-8)}</Link>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="关系">
            {data?.relationshipId ? (
              <Link to={`/users/${data.relationshipId}`}>{data.relationshipId.slice(-8)}</Link>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="输入 / 输出消息">
            {data?.inputMessageId?.slice(-8) ?? "—"} / {data?.outputMessageId?.slice(-8) ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="召回的记忆" span={3}>
            {data?.recalledMemoryIds.length ? <Tags values={data.recalledMemoryIds} /> : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="召回的知识切片" span={3}>
            {data?.recalledChunkIds.length ? <Tags values={data.recalledChunkIds} /> : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="引入的站外事件" span={3}>
            {data?.contextChannelEventIds.length
              ? <Tags values={data.contextChannelEventIds} />
              : "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="模型调用" loading={isLoading} styles={{ body: { padding: 12 } }}>
        {data?.promptRedacted && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Prompt 原文已隐藏"
            description="查看需要 prompt.read 权限。该字段含角色设定全文与用户上下文，默认只有超级管理员可见，且查看会记入审计。"
          />
        )}
        <Collapse
          size="small"
          items={(data?.calls ?? []).map((c) => ({
            key: c.id,
            label: (
              <Space wrap>
                <Tag>round {c.round}</Tag>
                <Tag color="blue">{c.purpose}</Tag>
                <Typography.Text code>{c.capability}</Typography.Text>
                <Typography.Text strong>{c.resolvedModel}</Typography.Text>
                <Typography.Text type="secondary">
                  {fmtNumber(c.inputTokens)}（缓存 {fmtNumber(c.cachedTokens)}）→ {fmtNumber(c.outputTokens)} tok ·{" "}
                  {fmtCents(c.costCents)} · {c.latencyMs}ms
                </Typography.Text>
                {c.errorCode && <Tag color="red">{c.errorCode}</Tag>}
              </Space>
            ),
            children: (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Descriptions size="small" column={4} bordered>
                  <Descriptions.Item label="结束原因">{c.finishReason ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="system prompt 长度">{fmtNumber(c.systemPromptLength)}</Descriptions.Item>
                  <Descriptions.Item label="task prompt 长度">{fmtNumber(c.taskPromptLength)}</Descriptions.Item>
                  <Descriptions.Item label="保留至">{fmtDateTime(c.retentionUntil)}</Descriptions.Item>
                </Descriptions>

                {c.systemPrompt !== null && (
                  <div>
                    <Typography.Text strong>System Prompt</Typography.Text>
                    <div className="mono" style={{ background: "#fafafa", padding: 10, borderRadius: 6, marginTop: 4 }}>
                      {c.systemPrompt}
                    </div>
                  </div>
                )}
                {c.taskPrompt !== null && (
                  <div>
                    <Typography.Text strong>Task Prompt</Typography.Text>
                    <div className="mono" style={{ background: "#fafafa", padding: 10, borderRadius: 6, marginTop: 4 }}>
                      {c.taskPrompt}
                    </div>
                  </div>
                )}
                {c.analysis != null && (
                  <div>
                    <Typography.Text strong>模型结构化输出</Typography.Text>
                    <div className="mono" style={{ background: "#fafafa", padding: 10, borderRadius: 6, marginTop: 4 }}>
                      {JSON.stringify(c.analysis, null, 2)}
                    </div>
                  </div>
                )}
              </Space>
            ),
          }))}
        />
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={8}>
          <Card title="Skill 执行" size="small" loading={isLoading}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              locale={{ emptyText: "无 Skill 执行" }}
              dataSource={data?.skillExecutions ?? []}
              columns={[
                { title: "Skill", dataIndex: "skillKey" },
                { title: "分级", dataIndex: "tier", width: 90 },
                { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
                { title: "预授权/结算", render: (_, r) => `${r.reservedPower}/${r.settledPower}`, width: 100 },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Tool 执行" size="small" loading={isLoading}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              locale={{ emptyText: "无 Tool 执行" }}
              dataSource={data?.toolExecutions ?? []}
              columns={[
                { title: "轮次", dataIndex: "round", width: 60 },
                { title: "Tool", dataIndex: "toolKey" },
                { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
                { title: "延迟", dataIndex: "latencyMs", width: 80, render: (v: number) => `${v}ms` },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="审核判定" size="small" loading={isLoading}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              locale={{ emptyText: "无审核判定" }}
              dataSource={data?.moderationDecisions ?? []}
              columns={[
                { title: "对象", dataIndex: "targetType", width: 100 },
                { title: "结论", dataIndex: "verdict", width: 80, render: (v) => <StatusTag value={v} /> },
                { title: "类别", dataIndex: "categories", render: (v: string[]) => <Tags values={v} /> },
                { title: "判定方", dataIndex: "decidedBy", width: 100 },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
